import Axios from '@/utils/axios';
import { getBackendErrorMessage } from '@/utils/errorHandler';
import { HandoverChecklistItemInput } from '@/services/api/handovers';

// ─── Enums ───────────────────────────────────────────────────────────────────

export enum IncidentStatus {
  OPEN = 'OPEN',
  IN_PROGRESS = 'IN_PROGRESS',
  RESOLVED = 'RESOLVED',
  CLOSED = 'CLOSED',
}

export enum IncidentSeverity {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL',
}

export enum IncidentType {
  ACCIDENT_DAMAGE = 'ACCIDENT_DAMAGE',
  USAGE_DAMAGE = 'USAGE_DAMAGE',
  LOST_UNIT = 'LOST_UNIT',
  MINOR_DAMAGE = 'MINOR_DAMAGE',
}

// ─── Interfaces ───────────────────────────────────────────────────────────────

export interface Incident {
  ID: string;
  code: string;
  handoverID: string;
  handoverStatus?: string;
  vehicleID: string;
  platNomor: string;
  oemID: string;
  oemName: string;
  modelID: string;
  modelName: string;
  driverCode: string;
  driverName: string;
  driverID?: number;
  clientID: number;
  clientName: string;
  hubID: number;
  hubName: string;
  incidentDate: string;
  incidentType: string;
  severity: IncidentSeverity;
  chronology?: string;
  costResponsibility: string;
  status: IncidentStatus;
  linkedMaintenanceCode?: string;
  linkedMaintenanceID?: string;
  maintenanceID?: string;
  maintenanceCode?: string;
  maintenanceLink?: string;
  damageImageURL?: string;
  imageURL?: string;
  policeReportURL?: string;
  notes?: string;
  createdAt: string;
  createdBy?: string;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

export interface IncidentListRequest {
  page?: string;
  size?: string;
  search?: string;
  statuses?: string;
  severities?: string;
  categories?: string;
  issueTypes?: string;
  oemIDs?: string;
  modelIDs?: string;
  startDate?: string;
  endDate?: string;
}

export interface IncidentListResponse {
  data: Incident[];
  pagination: {
    total: number;
    page: number;
    size: number;
    lastPage: number;
  };
}

export interface IncidentResponse {
  message: string;
  data?: Incident;
}

export interface CreateIncidentRequest {
  handoverID: string;
  incidentType: string;
  incidentDate: string;
  severity: IncidentSeverity | string;
  chronology?: string;
  costResponsibility: string;
  imageURL?: string;
  damageImageURL?: string;
  policeReportURL?: string;
  notes?: string;
  driverID?: number;
  driverCode?: string;
  driverName?: string;
  clientID?: number;
  clientName?: string;
  oemID?: string;
  createdBy: string;
  returnDate?: string;
  returnOdometer?: number;
  checklistItems?: HandoverChecklistItemInput[];
  returnCondition?: string;
  returnReason?: string;
  returnReasonOther?: string;
  vehicleReturnImageURL?: string;
  returnBastLink?: string;
  returnNotes?: string;
  vehicleAndMitraReturnImageURL?: string;
}

export interface UpdateIncidentRequest {
  severity?: IncidentSeverity | string;
  incidentType?: string;
  incidentDate?: string;
  chronology?: string;
  costResponsibility?: string;
  imageURL?: string;
  damageImageURL?: string;
  policeReportURL?: string;
  notes?: string;
  oemID?: string;
  driverID?: number;
  driverCode?: string;
  driverName?: string;
  clientID?: number;
  clientName?: string;
  updatedBy: string;
}

export interface MarkIncidentRequest {
  updatedBy: string;
}

export interface IncidentStatusHistoryApi {
  ID: string;
  incidentID: string;
  fromStatus?: IncidentStatus | string;
  toStatus?: IncidentStatus | string;
  reason?: string | null;
  metadata?: Record<string, unknown> | null;
  createdBy?: string;
  createdAt?: string;
}

// ─── API functions ────────────────────────────────────────────────────────────

export const getListIncidents = async (
  params?: IncidentListRequest
): Promise<IncidentListResponse> => {
  try {
    const response = await Axios.get('/v1/incidents', { params });
    return response.data;
  } catch (error: unknown) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

export const getIncidentById = async (id: string): Promise<Incident> => {
  try {
    const response = await Axios.get(`/v1/incidents/${id}`);
    return response.data.data || response.data;
  } catch (error: unknown) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

export const getIncidentStatusHistory = async (
  id: string
): Promise<IncidentStatusHistoryApi[]> => {
  try {
    const response = await Axios.get(`/v1/incidents/${id}/status-history`);
    const data = response.data.data || response.data;
    return Array.isArray(data) ? data : [];
  } catch (error: unknown) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

export const createIncident = async (
  data: CreateIncidentRequest
): Promise<IncidentResponse> => {
  try {
    const response = await Axios.post('/v1/incidents', data);
    return response.data;
  } catch (error: unknown) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

export const updateIncident = async (
  id: string,
  data: UpdateIncidentRequest
): Promise<IncidentResponse> => {
  try {
    const { imageURL, damageImageURL, ...rest } = data;
    const payload = {
      ...rest,
      ...(damageImageURL !== undefined
        ? { damageImageURL }
        : imageURL !== undefined
        ? { damageImageURL: imageURL }
        : {}),
    };
    const response = await Axios.patch(`/v1/incidents/${id}`, payload);
    return response.data;
  } catch (error: unknown) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};

export const markIncidentAsClose = async (
  id: string,
  data: MarkIncidentRequest
): Promise<IncidentResponse> => {
  try {
    const response = await Axios.patch(`/v1/incidents/${id}/mark-as-close`, data);
    return response.data;
  } catch (error: unknown) {
    const backendMessage = getBackendErrorMessage(error);
    throw new Error(backendMessage);
  }
};
